import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export const defaultNS = 'common';

/**
 * The languages the platform ships, English being the source. They mirror
 * `apps.accounts.models.Language` in the backend: adding one is a new
 * `locales/<code>/` folder with the same files as `locales/en/`, the code
 * here and a label in `LANGUAGE_LABELS`, plus the backend choice.
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];

/** Each language named in itself, for the switcher. */
export const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
};

/** The localStorage key i18next caches the choice under before sign-in. */
export const LANGUAGE_STORAGE_KEY = 'sentientos.language';

/**
 * One `common` namespace per language, assembled from one file per area
 * (`locales/<lng>/<area>.json`, each holding a single top-level key such as
 * `workspace` or `settings`). Splitting by file keeps the bundles reviewable;
 * a top-level key defined twice would silently shadow, so it fails loudly.
 */
const bundleFiles = import.meta.glob('./locales/*/*.json', { eager: true, import: 'default' });

function buildResources(files) {
  const assembled = {};
  for (const [path, bundle] of Object.entries(files)) {
    const [, language] = path.match(/locales\/(\w+)\//);
    assembled[language] ??= { common: {} };
    for (const [key, value] of Object.entries(bundle)) {
      if (key in assembled[language].common) {
        throw new Error(`i18n: top-level key "${key}" is defined twice for "${language}" (${path})`);
      }
      assembled[language].common[key] = value;
    }
  }
  return assembled;
}

export const resources = buildResources(bundleFiles);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // No hardcoded `lng`: before sign-in the detector takes the cached choice,
    // then the browser language. Once signed in, `User.language` wins (see the
    // auth listener in core/store).
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // `fr-FR` / `es-ES` from the browser resolve to the bare code we ship.
    load: 'languageOnly',
    ns: [defaultNS],
    defaultNS,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
  });

// Keep the document language in sync with the active language (a11y).
const syncHtmlLang = (lng) => {
  document.documentElement.lang = lng;
};
syncHtmlLang(i18n.resolvedLanguage);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
